import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const FractionCalculatorPage = () => {
  const [num1, setNum1] = useState('1');
  const [den1, setDen1] = useState('2');
  const [num2, setNum2] = useState('1');
  const [den2, setDen2] = useState('4');
  const [operation, setOperation] = useState('+');
  const [result, setResult] = useState(null);

  const [simplifyNum, setSimplifyNum] = useState('');
  const [simplifyDen, setSimplifyDen] = useState('');
  const [simplifyResult, setSimplifyResult] = useState(null);

  const [decimalInput, setDecimalInput] = useState('');
  const [decimalResult, setDecimalResult] = useState(null);

  const gcd = (a, b) => b === 0 ? Math.abs(a) : gcd(b, a % b);

  const simplify = (num, den) => {
    if (den === 0) return { num: 0, den: 1, error: 'Division by zero' };
    const divisor = gcd(num, den);
    let sNum = num / divisor;
    let sDen = den / divisor;
    if (sDen < 0) { sNum = -sNum; sDen = -sDen; }
    return { num: sNum, den: sDen };
  };

  const calculate = () => {
    const n1 = parseInt(num1) || 0;
    const d1 = parseInt(den1) || 1;
    const n2 = parseInt(num2) || 0;
    const d2 = parseInt(den2) || 1;

    let resNum, resDen;
    switch (operation) {
      case '+':
        resNum = n1 * d2 + n2 * d1;
        resDen = d1 * d2;
        break;
      case '-':
        resNum = n1 * d2 - n2 * d1;
        resDen = d1 * d2;
        break;
      case '×':
        resNum = n1 * n2;
        resDen = d1 * d2;
        break;
      case '÷':
        resNum = n1 * d2;
        resDen = d1 * n2;
        break;
      default:
        return;
    }

    const simplified = simplify(resNum, resDen);
    const decimal = simplified.den !== 0 ? (simplified.num / simplified.den).toFixed(6) : 'undefined';
    const whole = Math.floor(Math.abs(simplified.num) / simplified.den);
    const remainder = Math.abs(simplified.num) % simplified.den;
    const sign = simplified.num < 0 ? '-' : '';

    setResult({
      num: simplified.num,
      den: simplified.den,
      decimal,
      mixed: whole > 0 ? `${sign}${whole} ${remainder}/${simplified.den}` : null,
      original: { num: resNum, den: resDen }
    });
  };

  const handleSimplify = () => {
    const num = parseInt(simplifyNum) || 0;
    const den = parseInt(simplifyDen) || 1;
    const simplified = simplify(num, den);
    const decimal = simplified.den !== 0 ? (simplified.num / simplified.den).toFixed(6) : 'undefined';
    setSimplifyResult({ ...simplified, decimal });
  };

  const decimalToFraction = (decimal) => {
    if (isNaN(decimal)) return null;
    const str = decimal.toString();
    const decimalPlaces = str.includes('.') ? str.split('.')[1].length : 0;
    const denominator = Math.pow(10, decimalPlaces);
    const numerator = Math.round(decimal * denominator);
    return simplify(numerator, denominator);
  };

  const handleDecimalConvert = () => {
    const dec = parseFloat(decimalInput);
    if (!isNaN(dec)) {
      const frac = decimalToFraction(dec);
      setDecimalResult(frac);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/math" className="text-cyan-400 hover:text-cyan-300">Math</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Fraction Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-6">Fraction Calculator</h1>

          {/* Main Fraction Calculator */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4">Fraction Calculator</h2>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="flex flex-col items-center">
                <Input type="number" value={num1} onChange={(e) => setNum1(e.target.value)}
                  className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center mb-1" />
                <div className="w-20 h-0.5 bg-white"></div>
                <Input type="number" value={den1} onChange={(e) => setDen1(e.target.value)}
                  className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center mt-1" />
              </div>

              <select value={operation} onChange={(e) => setOperation(e.target.value)}
                className="px-4 py-3 bg-cyan-600 text-white rounded-lg font-bold text-xl">
                <option value="+">+</option>
                <option value="-">-</option>
                <option value="×">×</option>
                <option value="÷">÷</option>
              </select>

              <div className="flex flex-col items-center">
                <Input type="number" value={num2} onChange={(e) => setNum2(e.target.value)}
                  className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center mb-1" />
                <div className="w-20 h-0.5 bg-white"></div>
                <Input type="number" value={den2} onChange={(e) => setDen2(e.target.value)}
                  className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center mt-1" />
              </div>

              <Button onClick={calculate} className="bg-green-600 hover:bg-green-500 text-white text-xl px-6 py-3">=</Button>

              {result && (
                <div className="flex flex-col items-center bg-[#0d0d0d] p-4 rounded-lg">
                  <span className="text-green-400 font-bold text-2xl">{result.num}</span>
                  <div className="w-16 h-0.5 bg-green-400"></div>
                  <span className="text-green-400 font-bold text-2xl">{result.den}</span>
                </div>
              )}
            </div>

            {result && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="p-3 bg-[#0d0d0d] rounded-lg">
                  <p className="text-gray-400">Decimal</p>
                  <p className="text-white font-semibold">{result.decimal}</p>
                </div>
                {result.mixed && (
                  <div className="p-3 bg-[#0d0d0d] rounded-lg">
                    <p className="text-gray-400">Mixed Number</p>
                    <p className="text-white font-semibold">{result.mixed}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <AdInContent />

          {/* Simplify Fraction */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4">Simplify Fraction</h2>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-col items-center">
                <Input type="number" value={simplifyNum} onChange={(e) => setSimplifyNum(e.target.value)}
                  className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center mb-1" placeholder="num" />
                <div className="w-20 h-0.5 bg-white"></div>
                <Input type="number" value={simplifyDen} onChange={(e) => setSimplifyDen(e.target.value)}
                  className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center mt-1" placeholder="den" />
              </div>
              <Button onClick={handleSimplify} className="bg-cyan-600 hover:bg-cyan-500 text-white">Simplify</Button>
              {simplifyResult && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">=</span>
                  <div className="flex flex-col items-center bg-[#0d0d0d] p-3 rounded-lg">
                    <span className="text-green-400 font-bold">{simplifyResult.num}</span>
                    <div className="w-12 h-0.5 bg-green-400"></div>
                    <span className="text-green-400 font-bold">{simplifyResult.den}</span>
                  </div>
                  <span className="text-gray-400 ml-2">({simplifyResult.decimal})</span>
                </div>
              )}
            </div>
          </div>

          {/* Decimal to Fraction */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Decimal to Fraction</h2>
            <div className="flex flex-wrap items-center gap-4">
              <Input type="number" step="any" value={decimalInput} onChange={(e) => setDecimalInput(e.target.value)}
                className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" placeholder="0.75" />
              <Button onClick={handleDecimalConvert} className="bg-cyan-600 hover:bg-cyan-500 text-white">Convert</Button>
              {decimalResult && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">=</span>
                  <div className="flex flex-col items-center bg-[#0d0d0d] p-3 rounded-lg">
                    <span className="text-green-400 font-bold">{decimalResult.num}</span>
                    <div className="w-12 h-0.5 bg-green-400"></div>
                    <span className="text-green-400 font-bold">{decimalResult.den}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2 text-sm">
            <span className="text-gray-500">Related:</span>
            <Link to="/percentage-calculator" className="text-cyan-400 hover:text-cyan-300">Percentage</Link>
            <span className="text-gray-600">|</span>
            <Link to="/scientific-calculator" className="text-cyan-400 hover:text-cyan-300">Scientific</Link>
          </div>

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Math Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Scientific', path: '/scientific-calculator' }, { name: 'Percentage', path: '/percentage-calculator' }, { name: 'Triangle', path: '/triangle-calculator' }, { name: 'Standard Deviation', path: '/standard-deviation-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FractionCalculatorPage;
